import { Chip, Container, Stack } from '@mui/material'
import { VesselServiceResponseDto } from '@planning/app/api'
import { useTranslate } from '@tolgee/react'
import {
  ButtonOption,
  HighlightedText,
  OptionsButton,
  TomTable,
  tomTableColumnFilterPresets,
  useMinimalsTheme,
  useTomTable,
} from '@tom-ui/ui'
import { useTablePreferencesConfig } from '@tom-ui/utils'
import { MRT_ColumnDef } from 'material-react-table'
import { observer } from 'mobx-react-lite'
import { FC, useMemo } from 'react'
import { VesselServiceViewStore } from '../../Stores/VesselServiceViewStore'

interface IProps {
  store: VesselServiceViewStore
  onEdit: (item: VesselServiceResponseDto) => void
  onRemove: (item: VesselServiceResponseDto) => void
}

export const VesselServiceList: FC<IProps> = observer(({ store, onEdit, onRemove }) => {
  const { t } = useTranslate()
  const theme = useMinimalsTheme()

  const columns = useMemo<MRT_ColumnDef<VesselServiceResponseDto>[]>(
    () => [
      {
        accessorKey: 'serviceId',
        header: t('id', 'ID'),
        ...tomTableColumnFilterPresets.searchMandatory(),
      },
      {
        accessorKey: 'name',
        header: t('name', 'Name'),
        ...tomTableColumnFilterPresets.searchMandatory(),
      },
      {
        accessorKey: 'portRotation',
        header: t('unCodePortRotation', 'UN Code/Port rotation'),
        accessorFn: row => row.portRotation?.join(', '),
        Cell: cell => {
          const rotationItems = cell.row.original.portRotation
          const filterValue = cell.column.getFilterValue() as string | undefined
          return (
            <Stack
              direction='row'
              gap={theme.customSpacing.xs}
              flexWrap='wrap'
              py={theme.customSpacing.xs}
            >
              {rotationItems?.map(item => (
                <Chip
                  key={`service-${cell.row.original.serviceId}-${item}`}
                  label={
                    <HighlightedText highlight={filterValue} color={theme.palette.info.main}>
                      {item}
                    </HighlightedText>
                  }
                />
              ))}
            </Stack>
          )
        },
        ...tomTableColumnFilterPresets.searchMandatory(),
      },
    ],
    [t, theme],
  )

  const { savedPreferences, onPreferencesChange } = useTablePreferencesConfig(
    'planning/vessel-services',
  )

  const table = useTomTable(
    {
      columns,
      layoutMode: 'grid',
      data: store.items,
      enableRowSelection: false,
      enableTopToolbar: false,
      enableExpanding: false,
      muiTableContainerProps: {
        sx: {
          maxHeight: 'calc(100vh - 220px)',
          minHeight: 'calc(100vh - 220px)',
        },
      },
      renderRowActions: ({ row }) => {
        const options: ButtonOption[] = [
          {
            label: t('edit', 'Edit'),
            onClick: () => onEdit(row.original),
            testId: 'edit-vessel-service-option',
          },
          {
            label: t('remove', 'Remove'),
            onClick: () => onRemove(row.original),
            testId: 'remove-vessel-service-option',
          },
        ]
        return (
          <OptionsButton
            tooltip={t('moreOptions', 'More options')}
            options={options}
            position='right'
            testId='vessel-service-options-btn'
          />
        )
      },
    },
    undefined,
    undefined,
    { columns, savedPreferences, onPreferencesChange },
  )

  return (
    <Container
      sx={{
        paddingY: theme.customSpacing.m,
      }}
    >
      <TomTable table={table} />
    </Container>
  )
})
